import React from "react";
import {
  Box,
  Stack,
  Text,
  Button,
  Anchor,
  ThemeIcon,
  useMantineTheme,
} from "@mantine/core";
import {
  IconFingerprint,
  IconCheck,
  IconAlertTriangle,
} from "@tabler/icons-react";
import useLocale from "../hooks/useLocale";

const HERO_BY_STATE = {
  prompt: { Icon: IconFingerprint, tone: "neutral" },
  loading: { Icon: IconFingerprint, tone: "neutral" },
  success: { Icon: IconCheck, tone: "success" },
  error: { Icon: IconAlertTriangle, tone: "error" },
};

/**
 * The screen the user sees inside the top-level popup that's opened from
 * a cross-origin iframe for WebAuthn create(). They've already seen the
 * "Set up a passkey" pitch in the iframe and clicked through — this is
 * the focused confirmation step that lives in the popup. Intentionally
 * minimal: no benefit cards, no intro animation, no marketing copy —
 * just the prompt, the action button, and the matching loading/success/
 * error states.
 */
const PasskeyFinalizePrompt = ({
  state = "prompt",
  onContinue,
  onSkip,
}) => {
  const theme = useMantineTheme();
  const locale = useLocale("passkey");
  const copy = locale.finalize?.[state] || locale.finalize?.prompt || {};

  const mutedText =
    theme.colorScheme === "dark"
      ? theme.colors.dark[2]
      : theme.colors.gray[7];
  const accentColor =
    theme.colors[theme.primaryColor]?.[6] || theme.colors.blue[6];

  const heroConfig = HERO_BY_STATE[state] || HERO_BY_STATE.prompt;
  const HeroIcon = heroConfig.Icon;
  const heroBg =
    heroConfig.tone === "success"
      ? theme.colorScheme === "dark"
        ? "rgba(64, 192, 87, 0.18)"
        : theme.colors.green[1]
      : heroConfig.tone === "error"
        ? theme.colorScheme === "dark"
          ? "rgba(240, 62, 62, 0.18)"
          : theme.colors.red[1]
        : theme.colorScheme === "dark"
          ? "rgba(74, 222, 128, 0.15)"
          : "#dcfce7";
  const heroColor =
    heroConfig.tone === "success"
      ? theme.colorScheme === "dark"
        ? theme.colors.green[4]
        : theme.colors.green[7]
      : heroConfig.tone === "error"
        ? theme.colorScheme === "dark"
          ? theme.colors.red[4]
          : theme.colors.red[7]
        : theme.colorScheme === "dark"
          ? theme.white
          : "#000";

  const isLoading = state === "loading";
  const showCheckBadge = state === "prompt";

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100% + 20px)",
        margin: "-20px -25px 0 -25px",
      }}
    >
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "32px 24px 16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Box style={{ position: "relative", marginBottom: 20 }}>
          <ThemeIcon
            size={84}
            radius="xl"
            style={{ backgroundColor: heroBg, color: heroColor }}
          >
            <HeroIcon size={44} stroke={1.5} color={heroColor} />
          </ThemeIcon>
          {showCheckBadge && (
            <Box
              style={{
                position: "absolute",
                insetInlineEnd: -2,
                bottom: -2,
                width: 26,
                height: 26,
                borderRadius: "50%",
                backgroundColor: "#22c55e",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `2px solid ${
                  theme.colorScheme === "dark" ? theme.colors.dark[7] : "#fff"
                }`,
                boxShadow: "0 2px 6px rgba(34, 197, 94, 0.45)",
              }}
            >
              <IconCheck size={14} stroke={3} color="#000" />
            </Box>
          )}
        </Box>

        <Text fw={700} fz={22} lh={1.2} mb={8}>
          {copy.title}
        </Text>
        <Text fz={14} c={mutedText} lh={1.5} style={{ maxWidth: 320 }}>
          {copy.subtitle}
        </Text>
      </Box>

      <Box style={{ padding: "10px 24px 16px", flexShrink: 0 }}>
        <Stack spacing={8}>
          <Button
            size="lg"
            fullWidth
            onClick={onContinue}
            loading={isLoading}
            disabled={isLoading}
            leftIcon={
              state === "prompt" ? (
                <IconFingerprint size={20} stroke={1.8} />
              ) : undefined
            }
          >
            {copy.primaryButton}
          </Button>
          {copy.skipButton && !isLoading && (
            <Box style={{ textAlign: "center", marginTop: 4 }}>
              <Anchor
                component="button"
                type="button"
                onClick={onSkip}
                c={accentColor}
                fz={14}
                fw={500}
              >
                {copy.skipButton}
              </Anchor>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default PasskeyFinalizePrompt;
